/** Colours and sizes for the PDF Editor, kept apart from the screen so they can be tested. */
export const EDITOR_COLORS: { name: string; hex: string }[] = [
  { name: "Black", hex: "#000000" },
  { name: "Red", hex: "#E63333" },
  { name: "Orange", hex: "#F59E0B" },
  { name: "Yellow", hex: "#FFEB00" },
  { name: "Green", hex: "#16A34A" },
  { name: "Blue", hex: "#1A66E6" },
  { name: "Purple", hex: "#7C3AED" },
  { name: "White", hex: "#FFFFFF" },
];

export const DEFAULT_COLORS = {
  text: "#000000",
  box: "#FFEB00",
  rectangle: "#E63333",
  ellipse: "#E63333",
  freehand: "#1A66E6",
} as const;

export const TEXT_SIZES = [8, 10, 12, 14, 18, 24, 36, 48];
export const STROKE_WIDTHS = [1, 2, 3, 5, 8];

/** "#RRGGBB" to 0..1 channels; anything unreadable falls back to black. */
export function hexToRgb01(hex: string): { r: number; g: number; b: number } {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return { r: 0, g: 0, b: 0 };
  const n = parseInt(m[1], 16);
  return { r: ((n >> 16) & 255) / 255, g: ((n >> 8) & 255) / 255, b: (n & 255) / 255 };
}

/** Moves a point group by a fraction delta, keeping the whole thing on the page. */
export function shiftBox(x: number, y: number, w: number, h: number, dx: number, dy: number) {
  return {
    x: Math.min(Math.max(0, x + dx), Math.max(0, 1 - w)),
    y: Math.min(Math.max(0, y + dy), Math.max(0, 1 - h)),
  };
}

/** Undo / redo stack for a list of items. */
export interface History<T> { present: T; past: T[]; future: T[] }
export const newHistory = <T,>(present: T): History<T> => ({ present, past: [], future: [] });
export function pushHistory<T>(h: History<T>, next: T, limit = 100): History<T> {
  return { present: next, past: [...h.past, h.present].slice(-limit), future: [] };
}
export function replacePresent<T>(h: History<T>, next: T): History<T> { return { ...h, present: next }; }
export function undoHistory<T>(h: History<T>): History<T> {
  if (!h.past.length) return h;
  return { present: h.past[h.past.length - 1], past: h.past.slice(0, -1), future: [h.present, ...h.future] };
}
export function redoHistory<T>(h: History<T>): History<T> {
  if (!h.future.length) return h;
  return { present: h.future[0], past: [...h.past, h.present], future: h.future.slice(1) };
}
